import axios, {
	type AxiosError,
	type AxiosInstance,
	type AxiosRequestConfig,
	type AxiosResponse,
} from "axios";
import { config } from "@/config";

interface ApiConfig {
	baseURL: string;
	timeout?: number;
	headers?: Record<string, string>;
}

const DEFAULT_CONFIG: ApiConfig = {
	baseURL: config.urls.baseServiceUrl,
	timeout: 30000,
	headers: {
		"Content-Type": "application/json",
	},
};

export class ApiError extends Error {
	constructor(
		public status: number,
		public statusText: string,
		public data: unknown,
		public originalError?: AxiosError,
	) {
		super(`API Error: ${status} ${statusText}`);
		this.name = "ApiError";
	}
}

const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
	return response;
};

const errorInterceptor = async (error: AxiosError): Promise<never> => {
	const status = error.response?.status || 0;
	const statusText = error.response?.statusText || "Network Error";
	const data = error.response?.data;

	throw new ApiError(status, statusText, data, error);
};

const createApiClient = (config: Partial<ApiConfig> = {}): AxiosInstance => {
	const mergedConfig = { ...DEFAULT_CONFIG, ...config };

	const instance = axios.create({
		baseURL: mergedConfig.baseURL,
		timeout: mergedConfig.timeout,
		headers: mergedConfig.headers,
		// Send cookies with every request (user_auth_token is httpOnly)
		withCredentials: true,
	});

	// only for lcoal development dont push
	const devToken = import.meta.env.VITE_DEV_AUTH_TOKEN;
	if (devToken) {
		instance.interceptors.request.use((reqConfig) => {
			reqConfig.headers.set("Authorization", `Bearer ${devToken}`);
			return reqConfig;
		});
	}
	// Add response interceptor for error handling
	instance.interceptors.response.use(responseInterceptor, errorInterceptor);

	return instance;
};

export const apiClient = createApiClient();

export const createClient = (
	config: Partial<ApiConfig> = {},
): AxiosInstance => {
	return createApiClient(config);
};

export const api = {
	get: <T>(
		url: string,
		config?: AxiosRequestConfig,
	): Promise<AxiosResponse<T>> => apiClient.get<T>(url, config),

	post: <T>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<AxiosResponse<T>> => apiClient.post<T>(url, data, config),

	put: <T>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<AxiosResponse<T>> => apiClient.put<T>(url, data, config),

	patch: <T>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<AxiosResponse<T>> => apiClient.patch<T>(url, data, config),

	delete: <T>(
		url: string,
		config?: AxiosRequestConfig,
	): Promise<AxiosResponse<T>> => apiClient.delete<T>(url, config),
};

export type { ApiConfig, AxiosRequestConfig, AxiosResponse };
